import { t } from "ttag";

import ErrorBoundary from "metabase/ErrorBoundary";
import { useListDatabasesQuery } from "metabase/api";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { useSelector } from "metabase/lib/redux";
import { getHasNativeWrite } from "metabase/selectors/data";
import { getSetting } from "metabase/selectors/settings";
import { Center, Stack, Text, Title } from "metabase/ui";

import S from "./AdhocQueryPage.module.css";
import { AdhocWorkbench } from "./components/AdhocWorkbench";
import { AdhocWorkbenchProvider } from "./context";

/**
 * Route component for the ad-hoc query workbench.
 *
 * The permission check here is only about not showing a useless page — the real
 * gate is server side, on `POST /api/dataset`.
 */
export function AdhocQueryPage() {
  const { data, isLoading, error } = useListDatabasesQuery();
  const lastUsedDatabaseId = useSelector((state) =>
    getSetting(state, "last-used-native-database-id"),
  );

  if (isLoading || error) {
    return <LoadingAndErrorWrapper loading={isLoading} error={error} />;
  }

  const databases = data?.data ?? [];
  if (!getHasNativeWrite(databases)) {
    return <NoNativeWriteAccess />;
  }

  return (
    <ErrorBoundary>
      <div className={S.page} data-testid="adhoc-query-page">
        <AdhocWorkbenchProvider defaultDatabaseId={lastUsedDatabaseId ?? null}>
          <AdhocWorkbench databases={databases} />
        </AdhocWorkbenchProvider>
      </div>
    </ErrorBoundary>
  );
}

function NoNativeWriteAccess() {
  return (
    <Center h="100%" data-testid="adhoc-no-native-write">
      <Stack align="center" gap="sm" maw="25rem" ta="center">
        <Title order={3}>{t`You don't have permission to write queries`}</Title>
        <Text c="text-medium">
          {t`Ask an admin for native query access to a database to use the ad-hoc query workbench.`}
        </Text>
      </Stack>
    </Center>
  );
}
